import {UnderlineOutlined} from '@ant-design/icons';
import {useQueryClient} from '@tanstack/react-query';
import {Button, Form, Input, Select, Spin} from 'antd';
import {jwtDecode} from 'jwt-decode';
import {useEffect, useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RQGetUserDetails,
  RQUpdateUserProfileAPI,
} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';
import {LoginScreen} from '../../LoginScreen';

const PersonalInformation = () => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  const auth = useSelector(selectAuth);
  const {loggedIn} = useSelector(state => state.auth);

  const token = auth?.tokenData;
  const decodedToken = token ? jwtDecode(token) : null;
  const id = decodedToken?.userId;
  const queryClient = useQueryClient();

  const {
    data: userData,
    isLoading,
    isError,
    error,
  } = RQGetUserDetails(id, token, {
    enabled: !!loggedIn,
  });

  const {mutate: updateUserProfile} = RQUpdateUserProfileAPI();

  const INDIA_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
  ];

  useEffect(() => {
    if (!userData || isEditing) {return;}

    const values = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      mobile: userData.mobile,
      address: userData.address,
      street: userData.street,
      apartment: userData.apartment,
      pinCode: userData.pinCode,
      city: userData.city,
      district: userData.district,
      state: userData.state,
    };

    form.setFieldsValue(values);
    setInitialValues(values);
  }, [userData, isEditing]);

  const onFinish = values => {
    const formData = {
      ...values,
    };

    updateUserProfile(
      {token, formData, id},
      {
        onSuccess: () => {
          toast.success('Profile updated successfully!');

          queryClient.invalidateQueries(['getUserDetails', id]);

          setIsEditing(false);
        },
        onError: () => {
          toast.error('Failed to update profile');
        },
      },
    );
  };

  if (!loggedIn) {
    return <LoginScreen />;
  }

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center'}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return <div className={'text-red-400'}>{error?.message}</div>;
  }

  return (
    <div className={'flex flex-col text-white'}>
      <h1 className={'header'}>
        Personal Information
      </h1>

      <Form
        key={isEditing ? 'edit' : 'view'}
        form={form}
        layout={'vertical'}
        onFinish={onFinish}
      >

        <div className={'lg:flex gap-4'}>
          <Form.Item
            name={'firstName'}
            label={'First Name'}
            className={'flex-1'}
            rules={[
              {required: true, message: 'First name is required'},
              {whitespace: true, message: ''},
              {
                pattern: /^[A-Za-z]+$/,
                message: 'Only letters are allowed ',
              },
            ]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} />
          </Form.Item>

          <Form.Item
            name={'lastName'}
            label={'Last Name'}
            className={'flex-1'}
            rules={[
              {required: true, message: 'Please enter your last name'},
              {whitespace: true, message: ''},
              {
                pattern: /^[A-Za-z]+$/,
                message: 'Only letters are allowed',
              },
            ]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} />
          </Form.Item>
        </div>

        <div className={'lg:flex gap-4'}>
          <Form.Item name={'email'} label={'Email'} className={'flex-1'}>
            <Input disabled className={'!text-[#525252]'} />
          </Form.Item>

          <Form.Item
            name={'mobile'}
            label={'Contact No'}
            className={'flex-1'}
            rules={[
              {required: true, message: 'Enter contact number'},
              {pattern: /^[0-9]{10}$/, message: 'Contact number must be 10 digits'},
            ]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} maxLength={10} />
          </Form.Item>
        </div>

        <div className={'lg:flex gap-4'}>
          <Form.Item
            name={'street'}
            label={'Street'}
            className={'flex-1'}
            rules={[{required: true, message: 'Street is required'}]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} />
          </Form.Item>

          <Form.Item
            name={'apartment'}
            label={'Flat/ House no./ Building/ Company/ Apartment'}
            className={'flex-1'}
            rules={[{required: true, message: 'Apartment is required'}]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} />
          </Form.Item>
        </div>

        <div className={'lg:flex gap-4'}>
          <Form.Item
            name={'city'}
            label={'City'}
            className={'flex-1'}
            rules={[
              {
                required: true,
                message: 'City is required',
              },
              {
                pattern: /^[A-Za-z]+(?: [A-Za-z]+)*$/,
                message: 'Only letters allowed, no leading/trailing spaces',
              },
            ]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} />
          </Form.Item>

          <Form.Item
            name={'district'}
            label={'District'}
            className={'flex-1'}
            rules={[
              {required: true, message: 'District is required'},
              {pattern: /^[A-Za-z]+$/, message: 'Only letters allowed'},
            ]}
          >
            <Input disabled={!isEditing} className={'!text-[#525252]'} />
          </Form.Item>
        </div>

        <div className={'lg:flex gap-4'}>
          <Form.Item
            label={'State'}
            name={'state'}
            className={'flex-1'}   
            rules={[{required: true, message: 'Please select your state'}]}
          >
            <Select
              showSearch
              disabled={!isEditing}  
              className={'!text-[#525252]'}
              placeholder={'Select State / UT'}
              optionFilterProp={'children'}
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {INDIA_STATES.map(st => (
                <Select.Option key={st} value={st}>
                  {st}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name={'pinCode'}
            label={'Pincode'}
            className={'flex-1'}
            rules={[
              {required: true, message: 'Pincode is required'},
              {pattern: /^[1-9][0-9]{5}$/, message: 'Invalid pincode'},
            ]}
          >
            <Input disabled={!isEditing} maxLength={6} className={'!text-[#525252]'} />
          </Form.Item>
        </div>

        <Form.Item
          name={'address'}
          label={'Address'}
          rules={[{required: true, message: 'Please enter addres'},
            {whitespace: true, message: 'Address cannot be empty'},
          ]}
        >
          <Input.TextArea
            rows={4}
            disabled={!isEditing}
            className={'!text-[#525252]'}
          />
        </Form.Item>

        <div className={'flex justify-center mt-6 gap-4'}>
          {isEditing ? (
            <>
              <Button htmlType={'submit'} className={'rounded-md text-primary-50 bg-primary-800 hover:!bg-primary-900 px-10'}>
                Save
              </Button>

              <Button
                onClick={() => {
                  setIsEditing(false);
                  form.setFieldsValue(initialValues);
                }}
                className={'rounded-md px-10'}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              htmlType={'button'}
              onClick={() => setIsEditing(true)}
              className={'rounded-md px-10 font-medium text-primary-50 bg-primary-800 hover:!bg-primary-900'}
            >
            Edit
            </Button>

          )}
        </div>
      </Form>
    </div>
  );
};

export default PersonalInformation;
